import { useState, useEffect, useMemo, useRef } from "react";
import { useSignal } from "@preact/signals-react";
import { FormattedMessage as FM } from "react-intl";

import proxy from "api/proxy";
import { XMarkIcon, CheckCircleIcon } from "components/Icons";
import SelectionCardField from "components/MeForm/SelectionCardField";
import StdButton from "components/Buttons/StdButton";
import ProgressBar from "./ProgressBar";
import { Alert } from "componentsv2/widgets/Alert";
import { DataTable } from "apps/AppointmentScheduling/components/table/DataTable";
import {
  TextColumn,
  MoneyColumn,
  StatusColumn,
  MoneyInputColumn,
  TextInputColumn,
  fmtCOP,
} from "./TableColumns";

function pending(row) {
  return row.residual_amount || 0;
}

function ModalPayment({ closeModal, data, selectedRows }) {
  const [rows, setRows] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [finished, setFinished] = useState(false);
  const [processLog, setProcessLog] = useState([]);
  const [hasErrors, setHasErrors] = useState(false);
  const [hasPartial, setHasPartial] = useState(false);
  const [vouchers, setVouchers] = useState({});
  var statement = useSignal(null);

  const alertMsg = useSignal({
    show: false,
    text: null,
    icon: "xmark",
    color: "bg-red-200",
    autoRemove: true,
  });

  const selection = selectedRows ?? new Map();
  const ids = useMemo(() => Array.from(selection.keys()), [selection]);

  useEffect(() => {
    statement.value = null;
    if (ids.length > 0) {
      loadRows(ids);
    }
    return () => {
      statement.value = null;
    };
  }, [ids]);

  async function loadRows(currentIds) {
    if (!currentIds?.length) return;
    const { data: fresh } = await proxy.browse("sale.sale", currentIds, [
      "number",
      "total_amount",
      "residual_amount",
      "additional_paid_amount",
      "payment_method",
      "consumer.rec_name",
      "party.rec_name",
    ]);

    if (fresh) {
      setRows(
        fresh.map((rec) => ({
          ...rec,
          inputAmount: rec.residual_amount || 0,
        })),
      );
    }
  }

  function onChangeAmount(id, num) {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, inputAmount: num } : r)),
    );
  }

  function onChangeVoucher(id, value) {
    setVouchers((prev) => ({ ...prev, [id]: value }));
  }

  function validate() {
    for (const row of rows) {
      const max = pending(row);
      const toPay = row.inputAmount ?? max;

      if (toPay > max) {
        alertMsg.value = {
          show: true,
          text: `La factura ${row.number} supera lo que falta por cobrar (${fmtCOP(max)}).`,
          icon: "xmark",
          color: "bg-red-200",
          autoRemove: true,
        };
        return false;
      }

      if (toPay <= 0) {
        alertMsg.value = {
          show: true,
          text: `Ingresa un monto mayor a $0 para la factura ${row.number}.`,
          icon: "xmark",
          color: "bg-red-200",
          autoRemove: true,
        };
        return false;
      }
      if (statement.value?.["journal.require_voucher"]) {
        const number = vouchers[row.id];
        if (!number?.trim()) {
          alertMsg.value = {
            show: true,
            text: `La factura ${row.number} requiere número de comprobante.`,
            icon: "xmark",
            color: "bg-red-200",
            autoRemove: true,
          };
          return false;
        }
      }
    }
    return true;
  }
  function acceptModal() {
    if (!statement.value) {
      alertMsg.value = {
        show: true,
        text: "sale.sale.delivery.panel.modal.select_payment_required",
        icon: "xmark",
        color: "bg-red-200",
        autoRemove: true,
      };
      return;
    }
    if (!validate()) return;
    setProcessing(true);
  }

  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    if (!processing || finished) return;

    const processAll = async () => {
      const currentRows = rowsRef.current;
      const log = [];
      let errorsFound = false;

      for (let i = 0; i < currentRows.length; i++) {
        const row = currentRows[i];
        const pendingAmount = pending(row);
        const toPay = row.inputAmount ?? pendingAmount;

        try {
          const isPartial = toPay < pendingAmount;
          const { error } = await proxy.methodCall({
            model: "account.statement",
            method: "multipayment_invoices",
            args: [
              {
                statement_id: statement.value.id,
                sale_ids: [],
                payments: [
                  {
                    sale: row.id,
                    amount: toPay.toString(),
                    number: vouchers[row.id],
                  },
                ],
              },
            ],
          });

          if (error) throw new Error(error?.error || JSON.stringify(error));

          log.push({
            id: row.id,
            number: row.number,
            action: isPartial ? "partial" : "paid",
            amount: toPay,
            pending: isPartial ? pendingAmount - toPay : 0,
          });
        } catch (err) {
          errorsFound = true;
          let msg = err.message || "Error desconocido";
          try {
            const parsed = JSON.parse(msg);
            msg = parsed?.detail?.error || parsed?.error || msg;
          } catch (_) {}
          log.push({
            id: row.id,
            number: row.number,
            action: "error",
            amount: toPay,
            pending: pendingAmount,
            errorMsg: msg,
          });
        }
        setProgress(i + 1);
      }

      setProcessLog(log);
      setHasErrors(errorsFound);
      setHasPartial(log.some((entry) => entry.action === "partial"));
      setFinished(true);
      setTimeout(() => {
        setProcessing(false);
        closeModal();
      }, 1500);
    };

    processAll();
  }, [processing, finished, closeModal]);

  const totalPending = rows.reduce((acc, r) => acc + pending(r), 0);
  const totalToPay = rows.reduce(
    (acc, r) => acc + (r.inputAmount ?? pending(r)),
    0,
  );

  const fieldStatement = {
    name: "statement",
    model: "account.statement",
  };

  const paymentColorMap = {
    partial_paid: "bg-orange-100 text-orange-700",
    cash: "bg-green-100 text-green-700",
    terminal: "bg-blue-100 text-blue-700",
  };

  function getConsumerName(row) {
    return row["consumer."]?.rec_name || row["party."]?.rec_name || "\u2014";
  }

  function getVoucherValue(row) {
    return vouchers[row.id];
  }

  const columns = [
    <TextColumn
      key="number"
      field="number"
      labelId="sale.sale.delivery.panel.modal.column.factura"
      className="font-medium text-zinc-800"
      getValue={(row) => row.number || `#${row.id}`}
    />,
    <TextColumn
      key="consumer"
      field="consumer"
      labelId="sale.sale.delivery.panel.modal.column.consumidor"
      className="text-zinc-600 text-sm"
      getValue={getConsumerName}
    />,
    <MoneyColumn
      key="total_amount"
      field="total_amount"
      labelId="sale.sale.delivery.panel.modal.column.total"
    />,
    <StatusColumn
      key="payment_method"
      field="payment_method"
      labelId="sale.sale.delivery.panel.modal.column.payment_method"
      colorMap={paymentColorMap}
      fallbackColor="bg-zinc-100 text-zinc-500"
    />,
    <MoneyInputColumn
      key="amount_to_pay"
      field="amount_to_pay"
      labelId="sale.sale.delivery.panel.modal.column.a_pagar"
      onChangeAmount={onChangeAmount}
    />,
  ];

  if (statement.value?.["journal.require_voucher"]) {
    columns.push(
      <TextInputColumn
        key="voucher"
        field="voucher_number"
        labelId="sale.sale.delivery.panel.modal.column.voucher"
        getValue={getVoucherValue}
        onChange={onChangeVoucher}
        placeholder="No. voucher"
      />,
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl overflow-hidden">
      <div className="flex p-6 border-b border-zinc-100 justify-between items-center bg-zinc-50/50">
        <div>
          <h2 className="text-xl font-bold text-zinc-800">
            <FM id="sale.sale.delivery.panel.modal.title" />
          </h2>
          <p className="text-sm text-zinc-500"></p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-2 rounded-full hover:bg-zinc-200 transition-colors"
            onClick={closeModal}
          >
            <XMarkIcon width="24" className="text-zinc-400" />
          </button>
        </div>
      </div>

      <div className="grow overflow-auto p-8">
        {!processing ? (
          <div className="max-w-4xl mx-auto flex flex-col gap-8">
            <Alert
              {...alertMsg.value}
              onClose={() =>
                (alertMsg.value = { ...alertMsg.value, show: false })
              }
            />

            <div className="flex flex-col gap-4">
              <div className="text-sm tablet:text-base font-semibold text-zinc-700 uppercase tracking-tight">
                <FM id="sale.sale.delivery.panel.modal.select_payment" />
              </div>
              <div className="[&_span]:capitalize [&_span]:text-xs [&_span]:font-medium">
                <SelectionCardField
                  field={{ ...fieldStatement, options: data?.statements }}
                  data={statement}
                  attrs={{ level: "wizard", numCol: "2", min: true }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-8">
              <DataTable value={rows} className="rounded-xl border-zinc-200">
                {columns}
              </DataTable>

              <div className="bg-zinc-900 rounded-2xl p-6 text-white flex items-center justify-between border-b-4 border-amber-400">
                <div className="flex flex-col">
                  <span className="text-zinc-400 text-xs uppercase tracking-widest">
                    <FM id="sale.sale.delivery.panel.modal.total_to_collect" />
                  </span>
                  <span className="text-2xl font-black text-zinc-300">
                    {fmtCOP(totalPending)}
                  </span>
                </div>
                <div className="w-px h-10 bg-white/10" />
                <div className="flex flex-col items-end">
                  <span className="text-zinc-400 text-xs uppercase tracking-widest">
                    <FM id="sale.sale.delivery.panel.modal.to_collect_now" />
                  </span>
                  <span className="text-3xl font-black text-amber-400">
                    {fmtCOP(totalToPay)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 justify-center mt-2">
                <StdButton
                  key="ok"
                  color="amber"
                  size="w-full md:w-64"
                  height="h-12"
                  onClick={acceptModal}
                  style="text-base font-bold rounded-xl"
                >
                  <FM id="sale.sale.delivery.panel.modal.confirm_label" /> ·{" "}
                  {fmtCOP(totalToPay)}
                </StdButton>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-8 py-12">
            {!finished ? (
              <>
                <div className="relative">
                  <div className="w-24 h-24 rounded-full border-4 border-zinc-100 border-t-amber-500 animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center font-black text-zinc-900 text-xl">
                    {rows.length > 0
                      ? Math.round((progress / rows.length) * 100)
                      : 0}
                    %
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-zinc-800">
                    <FM id="sale.sale.delivery.panel.modal.processing_title" />
                  </h3>
                  <p className="text-zinc-500 mt-2">
                    <FM
                      id="sale.sale.delivery.panel.modal.processing_progress"
                      values={{ progress: progress, total: rows.length }}
                    />
                  </p>
                </div>
                <div className="w-full max-w-md">
                  <ProgressBar progress={progress} maxValue={rows.length} />
                </div>
              </>
            ) : (
              <>
                {hasErrors ? (
                  <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center text-red-800">
                    <XMarkIcon width="64" />
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                    <CheckCircleIcon width="64" />
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-zinc-800">
                    {hasErrors ? (
                      <FM id="sale.sale.delivery.panel.modal.error_title" />
                    ) : hasPartial ? (
                      <FM id="sale.sale.delivery.panel.modal.partial_title" />
                    ) : (
                      <FM id="sale.sale.delivery.panel.modal.completed_title" />
                    )}
                  </h3>
                  {hasErrors && (
                    <p className="text-sm text-zinc-500 mt-1">
                      <FM id="sale.sale.delivery.panel.modal.error_desc" />
                    </p>
                  )}
                </div>

                <div className="w-full max-w-lg flex flex-col gap-2">
                  {processLog.map((entry) => (
                    <div
                      key={entry.id}
                      className={`flex items-start justify-between px-4 py-3 rounded-xl text-sm font-semibold
                        ${
                          entry.action === "paid"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : entry.action === "partial"
                              ? "bg-orange-50 text-orange-700 border border-orange-200"
                              : "bg-red-50 text-red-700 border border-red-200"
                        }`}
                    >
                      <span className="font-bold shrink-0">
                        {entry.number || `#${entry.id}`}
                      </span>
                      <div className="flex flex-col items-end text-right ml-4">
                        <span>
                          {entry.action === "paid" ? (
                            <FM id="sale.sale.delivery.panel.modal.status_paid" />
                          ) : entry.action === "partial" ? (
                            <FM
                              id="sale.sale.delivery.panel.modal.status_abono"
                              values={{ amount: fmtCOP(entry.amount) }}
                            />
                          ) : (
                            <FM id="sale.sale.delivery.panel.modal.status_error" />
                          )}
                        </span>
                        {entry.action === "partial" && entry.pending > 0 && (
                          <span className="text-[11px] font-normal opacity-75">
                            <FM
                              id="sale.sale.delivery.panel.modal.pending"
                              values={{ amount: fmtCOP(entry.pending) }}
                            />
                          </span>
                        )}
                        {entry.action === "error" && entry.errorMsg && (
                          <span className="text-[11px] font-normal mt-1 max-w-xs text-right opacity-80">
                            {entry.errorMsg}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {hasErrors && (
                  <StdButton
                    key="close"
                    color="amber"
                    size="w-48"
                    height="h-11"
                    content="sale.sale.delivery.panel.modal.close"
                    onClick={closeModal}
                    style="text-base font-bold rounded-xl"
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ModalPayment;
